/**
 * «Найти в CRM по фото» (задача №374): модели CRM, похожие на фото папки, — из корпуса салонов
 * прокатa (`GET /api/dresses/{id}/crm/match`). По умолчанию — модели в наличии в городе, откуда
 * пришли фото; переключатель «все салоны». Карточка: совпавшее фото CRM, процент (среднее по фото
 * папки), модель и артикул, наличие по городам, остальные фото модели; «Привязать» — обычная
 * привязка серией экземпляра (в городе → в наличии → любой), не артикулом. Сходство — подсказка, решает человек.
 *
 * Первой строкой блока — полоса «Фото платья» (задача Оли №392: блок находок уводил сетку фото папки ниже
 * экрана, сравнивать было не с чем): фото папки превью, закреплена сверху, пока листаются карточки CRM.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { btn, btnPrimary, ErrorLine, Segmented } from "@/components/trends/ui";
import {
  bindArgs,
  cityOptions,
  cityValue,
  citiesLine,
  otherPhotos,
  percent,
  type CityMode,
  type MatchCard,
} from "@/lib/crmMatch";
import {
  bindDressCrm,
  loadDressCrm,
  loadDressCrmMatch,
  photoKeys,
} from "@/lib/photos";
import { mediaUrl, thumbUrl } from "@/lib/media";
import type { Photo } from "@/lib/photos";
import { retryQuery } from "@/lib/trends";

export function CrmMatch({ dressId, photos }: { dressId: number; photos: Photo[] }) {
  const [open, setOpen] = useState(false);
  if (!open) {
    return (
      <button
        type="button"
        className={btn}
        onClick={() => setOpen(true)}
        data-annot="найти в crm по фото"
      >
        Найти в CRM по фото
      </button>
    );
  }
  return <MatchBlock dressId={dressId} photos={photos} onClose={() => setOpen(false)} />;
}

/** «Фото платья» — то, с чем сравнивают находки; закреплена сверху блока. */
function DressStrip({ photos }: { photos: Photo[] }) {
  if (!photos.length) return null;
  return (
    <div
      className="sticky top-0 z-10 grid gap-1 border-b border-border bg-bg-subtle pb-2"
      data-annot="фото платья для сравнения"
    >
      <span className="text-[11.5px] text-fg-faint">Фото платья — сравнивайте с моделями CRM ниже</span>
      <div className="flex gap-1.5 overflow-x-auto">
        {photos.map((p) => {
          const path = p.view_path ?? p.asset_path;
          return (
            <a
              key={p.id}
              href={mediaUrl(path)}
              target="_blank"
              rel="noreferrer"
              title="Открыть оригинал"
              className="block h-40 shrink-0 overflow-hidden rounded bg-bg"
            >
              <img src={thumbUrl(path)} alt="" loading="lazy" className="h-full w-auto object-contain" />
            </a>
          );
        })}
      </div>
    </div>
  );
}

function MatchBlock({
  dressId,
  photos,
  onClose,
}: {
  dressId: number;
  photos: Photo[];
  onClose: () => void;
}) {
  const client = useQueryClient();
  const [city, setCity] = useState<CityMode>(null);
  const q = useQuery({
    queryKey: photoKeys.crmMatch(dressId, city),
    queryFn: () => loadDressCrmMatch(dressId, city),
    retry: retryQuery,
  });
  // уже привязано (в том числе к модели не из выдачи) — привязка другой просит подтверждения
  const link = useQuery({
    queryKey: photoKeys.crm(dressId),
    queryFn: () => loadDressCrm(dressId),
    retry: retryQuery,
  });
  const rebind = link.data?.crm_model_id != null;
  const bind = useMutation({
    mutationFn: async (card: MatchCard) => {
      const args = bindArgs(card);
      if (!args) throw new Error("у модели нет ни серии, ни артикула");
      const got = await bindDressCrm(dressId, args.series, args.modelId);
      if (got.status !== "bound") throw new Error(`CRM не привязала серию ${args.series}: ${got.status === "not_found" ? "не найдена" : "несколько моделей"}`);
      return got;
    },
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: photoKeys.crm(dressId) }),
        client.invalidateQueries({
          queryKey: ["photos", "dress", dressId, "crm-match"],
        }),
      ]),
  });

  const res = q.data;
  return (
    <div
      className="grid w-full gap-2 rounded-[var(--radius-ui)] border border-border bg-bg-subtle p-2"
      data-annot="поиск в crm по фото"
    >
      <DressStrip photos={photos} />
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[12.5px] text-fg">Похожие модели CRM</span>
        {res ? (
          <Segmented<string>
            label="Где"
            options={cityOptions(res)}
            value={cityValue(res)}
            onPick={(v) =>
              setCity(
                res.dress_city && v === String(res.dress_city.id) ? null : v,
              )
            }
          />
        ) : null}
        {res ? (
          <span className="text-[11.5px] text-fg-faint">
            фото с вектором {res.photos_with_vector} из {res.photos_total} ·
            процент — среднее по фото папки
          </span>
        ) : null}
        <button type="button" className={`${btn} ml-auto`} onClick={onClose}>
          Свернуть
        </button>
      </div>
      {q.isPending ? (
        <span className="text-[12.5px] text-fg-faint">Ищу похожие…</span>
      ) : null}
      <ErrorLine error={q.error} />
      <ErrorLine error={bind.error} />
      {res?.error ? (
        <span className="text-[12.5px] text-warn">{res.error}</span>
      ) : null}
      {res && !res.error && res.cards.length === 0 ? (
        <span className="text-[12.5px] text-fg-faint">
          Ничего похожего — попробуйте «все салоны».
        </span>
      ) : null}
      {res && res.cards.length ? (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(170px,1fr))] gap-2">
          {res.cards.map((c) => (
            <Card
              key={c.model_id}
              card={c}
              rebind={rebind}
              busy={bind.isPending || !link.isSuccess}
              onBind={() => bind.mutate(c)}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function Card({
  card,
  rebind,
  busy,
  onBind,
}: {
  card: MatchCard;
  rebind: boolean;
  busy: boolean;
  onBind: () => void;
}) {
  const [confirm, setConfirm] = useState(false);
  const args = bindArgs(card);
  const others = otherPhotos(card);
  return (
    <div
      className="grid content-start gap-1 rounded-[var(--radius-ui)] border border-border bg-bg p-1.5"
      data-annot="модель crm"
    >
      <a
        href={card.photo_url ?? undefined}
        target="_blank"
        rel="noreferrer"
        className="block aspect-[3/4] overflow-hidden rounded bg-bg-subtle"
      >
        {card.photo_url ? (
          <img
            src={card.photo_url}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover"
          />
        ) : null}
      </a>
      {others.length ? (
        <div className="flex gap-1">
          {others.map((u) => (
            <a
              key={u}
              href={u}
              target="_blank"
              rel="noreferrer"
              className="block h-12 w-9 overflow-hidden rounded bg-bg-subtle"
            >
              <img
                src={u}
                alt=""
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </a>
          ))}
        </div>
      ) : null}
      <span className="text-[12.5px] text-fg">
        <span className="tnum font-medium">{percent(card.score)}%</span> ·
        модель {card.name ?? `#${card.model_id}`}
      </span>
      <span className="text-[11.5px] text-fg-muted">
        арт. {card.article ?? "—"}
        {card.serial_here ? ` · серия здесь ${card.serial_here}` : ""}
      </span>
      <span className="text-[11.5px] text-fg-faint">
        {citiesLine(card.cities)}
      </span>
      {card.bound ? (
        <span className="text-[12px] text-ok">привязано</span>
      ) : (
        <button
          type="button"
          className={btnPrimary}
          disabled={busy || !args}
          onClick={() => {
            if (rebind && !confirm) return setConfirm(true);
            onBind();
          }}
        >
          {confirm ? "Перепривязать?" : "Привязать"}
        </button>
      )}
    </div>
  );
}
